"use client";

import { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import clsx from "clsx";
import { useTranslations } from "@/lib/compat/i18n";

/**
 * ShareDossierModal — small sub-modal opened from the TradeDossier
 * "Share dossier" CTA. Decorative — no real send. On click, the CTA
 * swaps to a "✓ Shared with N recipients" confirmation and auto-closes
 * after ~1.2 s.
 */

type Channel = "email" | "whatsapp" | "link";

const CHANNELS: { id: Channel; label: string }[] = [
  { id: "email",    label: "Email" },
  { id: "whatsapp", label: "WhatsApp" },
  { id: "link",     label: "Direct link" },
];

export function ShareDossierModal({
  open,
  onClose,
  tradeRef,
  defaultRecipients = "auditor@yourbank.com, customs@broker.com",
}: {
  open: boolean;
  onClose: () => void;
  tradeRef: string;
  defaultRecipients?: string;
}) {
  const t = useTranslations("shareDossierModal");
  const [recipients, setRecipients] = useState(defaultRecipients);
  const [channel, setChannel] = useState<Channel>("email");
  const [shared, setShared] = useState(false);

  // Reset state each time the modal opens.
  useEffect(() => {
    if (open) {
      setShared(false);
      setRecipients(defaultRecipients);
      setChannel("email");
    }
  }, [open, defaultRecipients]);

  const close = useCallback(() => onClose(), [onClose]);

  // Esc closes.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") close(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, close]);

  const recipientCount = recipients
    .split(",")
    .map((r) => r.trim())
    .filter((r) => r.length > 0).length;

  const share = useCallback(() => {
    if (shared) return;
    setShared(true);
    const t = setTimeout(() => onClose(), 1200);
    return () => clearTimeout(t);
  }, [shared, onClose]);

  if (!open) return null;
  if (typeof document === "undefined") return null;

  // Portal to document.body so the modal escapes any transformed
  // ancestor — see SignDocumentModal for the .scene-fade containing-
  // block bug this fixes.
  return createPortal(
    <div
      className="fixed inset-0 z-[60] grid place-items-center bg-orbitra-ink/80 backdrop-blur-sm"
      onClick={close}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="share-dossier-title"
        className="glass relative flex w-[min(90vw,520px)] flex-col gap-4 p-5"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          aria-label={t("closeAriaLabel")}
          onClick={close}
          className="absolute end-3 top-2 text-[18px] leading-none text-orbitra-muted hover:text-orbitra-paper"
        >
          ×
        </button>

        <div>
          <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-orbitra-teal-soft">
            {t("kicker")}
          </p>
          <h3 id="share-dossier-title" className="mt-1 font-display text-base font-semibold text-orbitra-paper">
            {tradeRef}
          </h3>
          <p className="mt-0.5 text-[11px] text-orbitra-muted">
            {t("body")}
          </p>
        </div>

        <div className="flex flex-col gap-1.5">
          <label htmlFor="recipients" className="font-mono text-[9px] uppercase tracking-[0.18em] text-orbitra-teal-soft">
            {t("recipientsLabel")}
          </label>
          <input
            id="recipients"
            type="text"
            value={recipients}
            onChange={(e) => setRecipients(e.target.value)}
            placeholder={t("recipientsPlaceholder")}
            className="rounded-md border border-state-verified/30 bg-orbitra-ink/60 px-3 py-2 font-mono text-[12px] text-orbitra-paper outline-none focus:border-state-verified/60 focus:ring-1 focus:ring-state-verified/40"
            disabled={shared}
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <span className="font-mono text-[9px] uppercase tracking-[0.18em] text-orbitra-teal-soft">
            Channel
          </span>
          <div className="flex flex-wrap gap-2">
            {CHANNELS.map((c) => (
              <button
                key={c.id}
                type="button"
                disabled={shared}
                onClick={() => setChannel(c.id)}
                className={clsx(
                  "rounded-full border px-3 py-1 text-[11px] transition-state",
                  channel === c.id
                    ? "border-state-verified bg-state-verified/15 text-state-verified shadow-[0_0_12px_var(--state-verified-glow)]"
                    : "border-orbitra-muted/30 bg-orbitra-ink/40 text-orbitra-muted hover:text-orbitra-paper hover:border-state-verified/40",
                  shared && "cursor-not-allowed opacity-50",
                )}
              >
                {c.label}
              </button>
            ))}
          </div>
        </div>

        <div className="flex items-center justify-between gap-3 pt-1">
          <p className="text-[10px] text-orbitra-muted">
            {recipientCount} recipient{recipientCount === 1 ? "" : "s"}
          </p>
          <button
            type="button"
            onClick={share}
            disabled={shared || recipientCount === 0}
            className={clsx(
              "px-5 py-2.5 font-display text-sm font-semibold transition-state",
              shared
                ? "rounded-full border border-policy-pass/40 bg-policy-pass/10 text-policy-pass shadow-[0_0_18px_var(--state-traced-glow)]"
                : "cta-primary",
              !shared && recipientCount === 0 && "cursor-not-allowed opacity-60",
            )}
          >
            {shared ? `✓ Shared with ${recipientCount} recipient${recipientCount === 1 ? "" : "s"}` : "Share dossier"}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
